// Dreams.tsx — the memories thirdeye's sessions keep about the user, to
// inspect, correct and delete.
//
// After each turn the harness distils what it learned into memories filed
// under one of three scopes (harness/src/memory/store.ts): about the user,
// about a repository, or about one session. Every later session recalls
// the ones that apply to it, so a wrong memory keeps steering the agent
// until someone fixes it here. Editing sets the memory's confidence to 1:
// the user's word is final.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { BsPencil, BsSave, BsSearch, BsTrash } from 'react-icons/bs';
import { ui } from 'common';
import { memoriesApi } from '../api';
import type { MemoryEdit, MemoryRow } from '../api';

interface Group {
  key: string;
  title: string;
  caption: string;
  rows: MemoryRow[];
}

/** Memories grouped the way recall uses them: you, then each repo, then each session. */
export function groupMemories(rows: MemoryRow[]): Group[] {
  const user: MemoryRow[] = [];
  const repos = new Map<string, MemoryRow[]>();
  const sessions = new Map<string, MemoryRow[]>();
  for (const r of rows) {
    if (r.scope === 'user') user.push(r);
    else if (r.scope === 'repo') repos.set(r.repo ?? '', [...(repos.get(r.repo ?? '') ?? []), r]);
    else sessions.set(r.session_id ?? '', [...(sessions.get(r.session_id ?? '') ?? []), r]);
  }
  const groups: Group[] = [];
  if (user.length) {
    groups.push({ key: 'user', title: 'About you', caption: 'Recalled by every session you start.', rows: user });
  }
  for (const [repo, rs] of [...repos].sort(([a], [b]) => a.localeCompare(b))) {
    groups.push({ key: `repo:${repo}`, title: repo, caption: 'Recalled by sessions with this repository checked out.', rows: rs });
  }
  for (const [id, rs] of sessions) {
    const name = rs[0]?.session_name || id;
    groups.push({ key: `session:${id}`, title: name, caption: 'Recalled by this session only.', rows: rs });
  }
  return groups;
}

function matches(r: MemoryRow, q: string): boolean {
  if (!q) return true;
  const hay = [r.memory.name, r.memory.description, r.memory.body, r.repo, r.session_name, ...(r.memory.tags ?? [])]
    .join('\n')
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

const SCOPE_VARIANT: Record<string, string> = { user: 'primary', repo: 'success', session: 'secondary' };

const Dreams: React.FC = () => {
  const [rows, setRows] = useState<MemoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<MemoryRow | null>(null);
  const [draft, setDraft] = useState<MemoryEdit>({ name: '', description: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = ui.useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await memoriesApi.list());
    } catch (err) {
      setPageError(`Loading memories failed: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => groupMemories(rows.filter(r => matches(r, query))), [rows, query]);

  const startEdit = (r: MemoryRow) => {
    setDraft({ name: r.memory.name, description: r.memory.description, body: r.memory.body });
    setEditing(r);
  };

  const save = async () => {
    if (!editing) return;
    if (!draft.name.trim() || !draft.body.trim()) {
      setPageError('A memory needs a name and a body.');
      return;
    }
    setSaving(true);
    try {
      await memoriesApi.update(editing, draft);
      setEditing(null);
      await load();
    } catch (err) {
      setPageError(`Saving the memory failed: ${err}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: MemoryRow) => {
    const ok = await confirm({
      title: 'Delete memory',
      body: `Delete "${r.memory.name}"? Sessions stop recalling it. A later session may learn it again.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await memoriesApi.delete(r);
      setRows(prev => prev.filter(x => !(x.scope === r.scope && x.key === r.key && x.memory.slug === r.memory.slug)));
    } catch (err) {
      setPageError(`Deleting the memory failed: ${err}`);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-2 gap-3 flex-wrap">
        <h2 className="text-info mb-0">DREAMS</h2>
        <Form.Group className="d-flex align-items-center gap-2" controlId="dreams-search">
          <BsSearch aria-hidden />
          <Form.Control
            type="search"
            placeholder="Search memories"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ minWidth: '16rem' }}
          />
        </Form.Group>
      </div>
      <p className="text-muted mb-4">
        What your sessions have learned, filed by who or what it is about. Sessions recall these before every turn —
        correct anything that is wrong.
      </p>

      {pageError && (
        <Alert variant="danger" dismissible onClose={() => setPageError('')}>
          {pageError}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="info" />
        </div>
      ) : groups.length === 0 ? (
        <p className="text-muted">
          {rows.length === 0 ? 'No memories yet. They appear after sessions have had a few turns.' : 'No memories match.'}
        </p>
      ) : (
        groups.map(g => (
          <section key={g.key} className="mb-5">
            <h4 className="mb-0">{g.title}</h4>
            <div className="small text-muted mb-3">
              {g.caption} {g.rows.length} {g.rows.length === 1 ? 'memory' : 'memories'}.
            </div>
            <Row className="g-4">
              {g.rows.map(r => (
                <Col key={`${r.scope}:${r.key}:${r.memory.slug}`} md={6} xl={4}>
                  <Card className="h-100 bg-dark">
                    <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                      <span className="fw-bold">{r.memory.name}</span>
                      <div className="d-flex gap-1">
                        <Badge bg={SCOPE_VARIANT[r.scope] ?? 'secondary'}>{r.scope}</Badge>
                        <Badge bg="dark" className="border border-secondary">{r.memory.kind}</Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="small text-muted mb-2">{r.memory.description}</div>
                      <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                        {r.memory.body}
                      </div>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">
                        confidence {r.memory.confidence.toFixed(2)} · {new Date(r.updated_at).toLocaleString()}
                      </span>
                      <div className="d-flex gap-2">
                        <Button variant="outline-info" size="sm" onClick={() => startEdit(r)}>
                          <BsPencil className="me-1" /> Edit
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => remove(r)}>
                          <BsTrash className="me-1" /> Delete
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        ))
      )}

      <Modal show={editing !== null} onHide={() => setEditing(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit memory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="dream-name">
              <Form.Label>Name</Form.Label>
              <Form.Control value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="dream-description">
              <Form.Label>Summary</Form.Label>
              <Form.Control value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
            </Form.Group>
            <Form.Group controlId="dream-body">
              <Form.Label>Memory (Markdown)</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={draft.body}
                onChange={e => setDraft({ ...draft, body: e.target.value })}
                className="font-monospace"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button variant="info" onClick={save} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : <BsSave className="me-2" />} Save
          </Button>
        </Modal.Footer>
      </Modal>

      {confirmDialog}
    </div>
  );
};

export default Dreams;
